/**
 * Histopathology Masters – E-Sign Configuration
 * Migrated from admin4.0 eSignConfig.js (functional component, Histopathology context).
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Button,
  Stack,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Box,
  InputAdornment,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Modal,
  ListItem,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider,
} from "@mui/material";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import "../../Styles/Configuration.css";
import "react-image-crop/dist/ReactCrop.css";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import ReactCrop from "react-image-crop";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { ImagePaths } from "../../Utility/ImagePaths";
import { Colors } from "../../Styles/Colors";
import { ConfirmPopup, DeletePopup } from "../../Components/Common Components/CommonComponents";
import { getCachevalue } from "../../Utility/Services/CacheProviderService";
import { CheckAccessFunc } from "../../Components/Common Components/DashboardComponents";
import { DateTime } from "luxon";

const ESIGN_ACCESS = () =>
  CheckAccessFunc("Histopathology", "Settings", "E-Sign Configuration", null, "Tab");

function alphanumValidation(value) {
  if (!value) return true;
  return /^[a-zA-Z0-9\s]*$/.test(value);
}

const defaultCrop = {
  unit: "%",
  x: 22.5,
  y: 30,
  width: 60,
  height: 25,
  aspect: 16 / 9,
};

export default function ESignConfiguration() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const [selectedIndex, setSelectedIndex] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [selectQualification, setSelectQualification] = useState("");
  const [selectRegNo, setSelectRegNo] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(true);
  const [imgUpload, setImgUpload] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedEditID, setSelectedEditID] = useState("");
  const [esignatureList, setEsignatureList] = useState([]);
  const [deletedapiID, setDeletedapiID] = useState("");
  const [histopathologyId, setHistopathologyId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState(defaultCrop);
  const [cropPop, setCropPop] = useState(false);
  const [fileName, setFileName] = useState("");
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);
  const [roleTypeList, setRoleTypeList] = useState([]);
  const [editRoleName, setEditRoleName] = useState(false);
  const [roleSelectID, setRoleSelectID] = useState(null);
  const [addRoleName, setAddRoleName] = useState("");
  const [addnewRoleType, setAddnewRoleType] = useState(false);
  const [renameRole, setRenameRole] = useState(false);
  const [deleteRole, setDeleteRole] = useState(false);
  const [deletedPopup, setDeletedPopup] = useState(false);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [imageURL, setImageURL] = useState("");
  const [isAdd, setIsAdd] = useState(false);

  const uploadImageRef = useRef(null);
  const imageRef = useRef(null);

  const editAccess = ESIGN_ACCESS()?.editAccess ?? false;

  const successMessage = useCallback((message) => {
    setSuccessMsg(true);
    setSuccessMsgText(message);
  }, []);

  const errorMessage = useCallback((message) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(message);
  }, []);

  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  }, []);

  const getSignatureData = useCallback(() => {
    setIsLoader(true);
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_ESIGN_CONFIG)
      .then((response) => {
        if (response?.data?.status === "success") {
          setEsignatureList(response.data.data ?? []);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        setIsErrorMsg(true);
        setIsErrorMsgText(e?.response?.data?.message || e?.message);
      });
  }, []);

  const getRoleMaster = useCallback(() => {
    setIsLoader(true);
    RestAPIService.getAll(Serviceurls.HISTO_ESIGN_ROLEADD)
      .then((response) => {
        if (response?.data?.status === "success") {
          setRoleTypeList(response?.data?.data ?? []);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  }, [errorMessage]);

  useEffect(() => {
    getSignatureData();
    getRoleMaster();
    const myUser = JSON.parse(getCachevalue("loggedInUserInfo") || "{}");
    setHistopathologyId(myUser?.histopathology_id ?? null);
    setOrgId(myUser?.org_id ?? null);
  }, [getSignatureData, getRoleMaster]);

  const getS3Key = useCallback(async (doc_key) => {
    try {
      const response = await RestAPIService.getAllTwo(
        Serviceurls.AWS_URL_GET + "?doc_key=" + encodeURIComponent(doc_key)
      );
      if (response?.data?.status === "success") {
        return response.data.data;
      }
    } catch (e) {
      errorMessage("Error fetching S3 URL");
    }
  }, [errorMessage]);

  const postSignatureData = useCallback(
    (filePath) => {
      const data = {
        name: (selectedName || "").trim(),
        role: selectedRole != null && selectedRole !== "" ? Number(selectedRole) : null,
        e_sign: filePath,
        status: typeof selectedStatus === "string" ? JSON.parse(selectedStatus) : !!selectedStatus,
        qualification: (selectQualification || "").trim(),
        reg_no: (selectRegNo || "").trim(),
      };
      if (selectedEditID) data.id = Number(selectedEditID);

      setIsLoader(true);
      RestAPIService.create(data, Serviceurls.HISTOPATHOLOGY_ESIGN_CONFIG)
        .then((response) => {
          if (response?.data?.status === "success") {
            successMessage(response.data.message);
            setIsLoader(false);
            getSignatureData();
            clearValue();
          }
        })
        .catch((e) => {
          setIsLoader(false);
          setIsErrorMsg(true);
          setIsErrorMsgText(e?.response?.data?.message || e?.message);
        });
    },
    [
      selectedName,
      selectedRole,
      selectedStatus,
      selectRegNo,
      selectQualification,
      selectedEditID,
      successMessage,
      getSignatureData,
    ]
  );

  const editCultureTestData = useCallback(
    async (item, index) => {
      let upload = [];
      if (item?.e_sign) {
        const val = await getS3Key(item.e_sign);
        if (val) upload.push(val);
      }
      setImgUpload(upload);
      setSelectedIndex(index);
      setSelectedRole(item?.role_id ?? "");
      setSelectedName(item?.name ?? "");
      setSelectedStatus(item?.status ?? true);
      setSelectedEditID(item?.id ?? "");
      setSelectedImage(upload[0] ?? null);
      setImageURL(item?.e_sign ?? "");
      setSelectQualification(item?.qualification ?? "");
      setSelectRegNo(item?.reg_no ?? "");
      setEditRoleName(!!item?.role_id);
      setRoleSelectID(item?.role_id ?? null);
      setAddRoleName(
        roleTypeList.find((r) => r.id === item?.role_id)?.name ?? ""
      );
    },
    [getS3Key, roleTypeList]
  );

  const deleteCultureData = useCallback(
    (ID) => {
      RestAPIService.deleteAll(Serviceurls.HISTOPATHOLOGY_ESIGN_CONFIG + "?id=" + ID)
        .then((response) => {
          if (response?.data?.status === "success") {
            successMessage(response.data.message);
            setDeletedPopup(false);
            getSignatureData();
          }
        })
        .catch((e) => {
          setIsErrorMsg(true);
          setIsErrorMsgText(e?.response?.data?.message || e?.message);
        });
    },
    [successMessage, getSignatureData]
  );

  const clearValue = useCallback(() => {
    setSearchText("");
    setSelectedRole("");
    setSelectedName("");
    setSelectedStatus(true);
    setImgUpload([]);
    setSelectedImage(null);
    setPopUpOpen(false);
    setSelectedEditID("");
    setSelectedIndex("");
    setFileName("");
    setSelectQualification("");
    setSelectRegNo("");
    setImageURL("");
    setCrop(defaultCrop);
    setCroppedImageUrl(null);
    setCropPop(false);
    setEditRoleName(false);
    setRoleSelectID(null);
    setAddRoleName("");
    if (uploadImageRef.current) uploadImageRef.current.value = "";
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  const removeDataList = useCallback(() => {
    deleteCultureData(deletedapiID);
    setDeletedPopup(false);
  }, [deletedapiID, deleteCultureData]);

  const filtereddata = (esignatureList ?? []).filter((data) => {
    const s = (searchText || "").toLowerCase();
    const roleMatch = (data.role_name ?? "").toLowerCase().includes(s);
    const nameMatch = (data.name ?? "").toLowerCase().includes(s);
    return roleMatch || nameMatch;
  });

  const onImageRemove = (index) => {
    if (selectedImage && typeof selectedImage === "string" && selectedImage.startsWith("blob:")) {
      URL.revokeObjectURL(selectedImage);
    }
    setPopUpOpen(false);
    setSelectedImage(null);
    const next = imgUpload.filter((_, i) => i !== index);
    setImgUpload(next);
  };

  const getCroppedImg = (image, cropVal, fName) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = cropVal.width;
    canvas.height = cropVal.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      cropVal.x * scaleX,
      cropVal.y * scaleY,
      cropVal.width * scaleX,
      cropVal.height * scaleY,
      0,
      0,
      cropVal.width,
      cropVal.height
    );
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        resolve(url);
      }, "image/jpeg");
    });
  };

  const onCropComplete = async (cropVal) => {
    if (imageRef.current && cropVal?.width && cropVal?.height) {
      const url = await getCroppedImg(imageRef.current, cropVal, fileName);
      setCroppedImageUrl(url);
    }
  };

  const onImageLoad = (e) => {
    imageRef.current = e.currentTarget;
  };

  const uploadCroppedImg = () => {
    if (!croppedImageUrl) return;
    fetch(croppedImageUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], fileName || "signature.jpg", {
          type: blob.type,
        });
        setImgUpload([file]);
        setCrop(defaultCrop);
        setCroppedImageUrl(null);
      })
      .catch(() => {});
  };

  const handleFileChange = (e) => {
    if (e.target.files?.length > 0) {
      const file = e.target.files[0];
      setFileName(file?.name ?? "");
      const reader = new FileReader();
      reader.onload = () => setSrc(reader.result);
      reader.readAsDataURL(file);
      setCropPop(true);
    }
    if (uploadImageRef.current) uploadImageRef.current.value = "";
  };

  const handleCropClose = () => {
    setSrc(null);
    setCropPop(false);
    setCroppedImageUrl(null);
    setCrop(defaultCrop);
    setFileName("");
  };

  const s3FileUpload = useCallback(
    (item = []) => {
      try {
        if (!item?.length) {
          errorMessage("Please add at least one file");
          setDisableBtn(false);
          return;
        }
        for (let i = 0; i < item.length; i++) {
          let filePath = "";
          const currentDate = new Date();
          if (item[i].name) {
            filePath =
              "histopathology/" + (orgId ?? "") + "/" + (histopathologyId ?? "");
          } else {
            if (selectedEditID) filePath = imageURL ?? "";
          }
          const data = new FormData();
          data.append("file_data", item[i]);
          data.append("folder_path", filePath);
          data.append(
            "current_date",
            DateTime.fromJSDate(currentDate).toFormat("yyyy-MM-dd HH:mm:ss")
          );
          setIsLoader(true);
          setDisableBtn(true);
          RestAPIService.postFormData(data, Serviceurls.LAB_ESIGN_UPLOAD)
            .then((response) => {
              if (response?.data?.status === "success") {
                successMessage("File uploaded successfully");
                const path = response.data?.data;
                postSignatureData(path);
              }
              setIsLoader(false);
              setDisableBtn(false);
            })
            .catch((err) => {
              setIsLoader(false);
              setDisableBtn(false);
              errorMessage(err?.response?.data?.message || err?.message || "File Upload Failed");
            });
          break;
        }
      } catch (err) {
        setIsLoader(false);
        setDisableBtn(false);
        errorMessage(err?.message || "File Upload Failed");
      }
    },
    [
      selectedEditID,
      imageURL,
      orgId,
      histopathologyId,
      errorMessage,
      successMessage,
      postSignatureData,
    ]
  );

  const validation = () => {
    if (!imgUpload?.length) {
      errorMessage("Please add at least one file");
      return;
    }
    if (!selectedName?.trim()) {
      errorMessage("Please enter the name");
      return;
    }
    if (!selectedRole) {
      errorMessage("Please select the role");
      return;
    }
    if (!selectQualification?.trim()) {
      errorMessage("Please enter the qualification");
      return;
    }
    if (!selectRegNo?.trim()) {
      errorMessage("Please enter the registration no");
      return;
    }
    if (!imgUpload[0]?.name && selectedEditID && imageURL) {
      postSignatureData(imageURL);
    } else {
      s3FileUpload(imgUpload);
    }
  };

  const sendRoleMaster = useCallback(() => {
    if (!addRoleName?.trim()) {
      errorMessage("Please Enter the Role Name");
      return;
    }
    setIsAdd(true);
    const data = { name: addRoleName.trim(), status: true };
    if (renameRole && roleSelectID) data.id = Number(roleSelectID);
    RestAPIService.create(data, Serviceurls.HISTO_ESIGN_ROLEADD)
      .then((response) => {
        if (response?.data?.status === "success") {
          successMessage(response.data.message);
          setAddnewRoleType(false);
          setAddRoleName("");
          setEditRoleName(false);
          setRenameRole(false);
          setIsAdd(false);
          getRoleMaster();
        }
      })
      .catch((e) => {
        setIsAdd(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  }, [addRoleName, renameRole, roleSelectID, errorMessage, successMessage, getRoleMaster]);

  const deleteRoleApi = useCallback(() => {
    if (!roleSelectID) return;
    setIsLoader(true);
    RestAPIService.delete(Serviceurls.HISTO_ESIGN_ROLEADD + "?id=" + roleSelectID)
      .then((response) => {
        if (response?.data?.status === "success") {
          setDeleteRole(false);
          setRoleSelectID(null);
          setSelectedRole("");
          setAddRoleName("");
          setEditRoleName(false);
          getRoleMaster();
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  }, [roleSelectID, errorMessage, getRoleMaster]);

  const selectedRoleOption =
    roleTypeList.find((r) => r.id === selectedRole) || null;

  return (
    <div className="eMed_TestConfig_MainContainer">
      <div className="eMed_TestConfig_Con">
        <div className="eMed_TestConfig_leftSide">
          <Paper elevation={0}>
            <Box className="eMed_Search_Box_">
              <TextField
                variant="standard"
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "15vw" }}
              />
            </Box>
            <TableContainer className="eMed_ConfigTable_Paper">
              <Table stickyHeader size="small">
                <TableHead sx={{ height: "7.3vh" }}>
                  <TableRow>
                    <TableCell
                      id="eMed_config_TableRow1"
                      align="left"
                      sx={{ color: Colors.HeadText }}
                    >
                      S.No
                    </TableCell>
                    <TableCell
                      id="eMed_config_TableRow"
                      align="left"
                      sx={{ color: Colors.HeadText }}
                    >
                      Role
                    </TableCell>
                    <TableCell
                      id="eMed_config_TableRow"
                      align="left"
                      sx={{ color: Colors.HeadText }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      id="eMed_config_TableRow"
                      align="left"
                      sx={{ color: Colors.HeadText }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      id="eMed_config_TableRow"
                      align="center"
                      sx={{ color: Colors.HeadText }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                {isLoader ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      position: "relative",
                      left: "22vw",
                      top: "14vw",
                    }}
                  >
                    <CircularProgress color="primary" />
                  </Box>
                ) : (
                  filtereddata
                    .slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    .map((data, index) => {
                      const sno = page * rowsPerPage + (index + 1);
                      const isSelected = selectedEditID && data.id === selectedEditID;
                      return (
                        <TableBody key={data.id ?? index}>
                          <TableRow
                            sx={{
                              bgcolor: isSelected ? "#e2e2e2" : "white",
                            }}
                          >
                            <TableCell align="left">{sno}</TableCell>
                            <TableCell align="left">
                              {data?.role_name?.length > 20 ? (
                                <Tooltip
                                  title={data?.role_name}
                                  placement="top"
                                  arrow
                                >
                                  <div>
                                    {data?.role_name?.slice(0, 20)}...
                                  </div>
                                </Tooltip>
                              ) : (
                                <div>{data?.role_name}</div>
                              )}
                            </TableCell>
                            <TableCell align="left">
                              {data?.name?.length > 20 ? (
                                <Tooltip
                                  title={data?.name}
                                  placement="top"
                                  arrow
                                >
                                  <div>
                                    {data?.name?.slice(0, 20)}...
                                  </div>
                                </Tooltip>
                              ) : (
                                <div>{data?.name}</div>
                              )}
                            </TableCell>
                            <TableCell align="left">
                              {data.status === true ? "Active" : "Inactive"}
                            </TableCell>
                            <TableCell align="center">
                              <div className="eMed_TestConfig_Con">
                                <IconButton
                                  size="small"
                                  disabled={!editAccess}
                                  onClick={() =>
                                    editCultureTestData(data, index)
                                  }
                                >
                                  <img
                                    src={
                                      editAccess
                                        ? ImagePaths.Edit?.default ??
                                          ImagePaths.Edit
                                        : ImagePaths.DisabledEdit?.default ??
                                          ImagePaths.DisabledEdit
                                    }
                                    className="eMed_TestConfig_DeleteIcon"
                                    alt="Edit"
                                  />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={!editAccess}
                                  onClick={() => {
                                    setDeletedPopup(true);
                                    setDeletedapiID(data.id);
                                  }}
                                >
                                  <img
                                    src={
                                      editAccess
                                        ? ImagePaths.Delete?.default ??
                                          ImagePaths.Delete
                                        : ImagePaths.DisabledDelete?.default ??
                                          ImagePaths.DisabledDelete
                                    }
                                    className="eMed_TestConfig_DeleteIcon"
                                    alt="Delete"
                                  />
                                </IconButton>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      );
                    })
                )}
              </Table>
              {filtereddata?.length === 0 && (
                <div className="eMed_TestConfig_NoRecords">
                  No records to display
                </div>
              )}
            </TableContainer>
            <div className="eMed_TestConfig_LeftPageination">
              <TablePagination
                component="div"
                id="eMed_tbl_pagination"
                rowsPerPageOptions={[10, 20, 30]}
                count={filtereddata?.length ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </Paper>
        </div>

        <div className="eMed_TestConfig_RightSide">
          <div
            className="eMed_TestConfig_RightHead"
            style={{ height: "5.5vh" }}
          >
            <p className="eMed_Testconfig_RightHead_text">
              Histopathology E-Signature
            </p>
          </div>
          <div className="eMed_CulturalConfig_RightContentBox">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                padding: "0.2vw",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Autocomplete
                size="small"
                sx={{ width: "14vw" }}
                value={selectedRoleOption}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedRole(newValue.id);
                    setEditRoleName(true);
                    setRoleSelectID(newValue.id);
                    setAddRoleName(newValue.name ?? "");
                  } else {
                    setSelectedRole("");
                    setEditRoleName(false);
                    setRoleSelectID(null);
                    setAddRoleName("");
                  }
                }}
                options={roleTypeList}
                getOptionLabel={(option) => option.name ?? ""}
                renderInput={(params) => (
                  <TextField {...params} label="Select Role" />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: "1.3",
                    }}
                  >
                    {option.name}
                  </li>
                )}
                PaperComponent={({ children }) => (
                  <Box
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{ backgroundColor: "white", boxShadow: 3 }}
                  >
                    {children}
                    <Divider />
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddnewRoleType(true);
                          setAddRoleName("");
                          setRenameRole(false);
                          setRoleSelectID(null);
                        }}
                      >
                        <ListItemText primary="Add Role Name" />
                      </ListItemButton>
                    </ListItem>
                    {editRoleName && (
                      <>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddnewRoleType(true);
                              setRenameRole(true);
                            }}
                          >
                            <ListItemText primary="Rename" />
                          </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteRole(true);
                            }}
                          >
                            <ListItemText primary="Delete" />
                          </ListItemButton>
                        </ListItem>
                      </>
                    )}
                  </Box>
                )}
              />
              <TextField
                size="small"
                variant="outlined"
                label="Name"
                value={selectedName}
                style={{ width: "14vw" }}
                onChange={(e) => setSelectedName(e.target.value)}
                inputProps={{ maxLength: 60 }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                padding: "0.3vw",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                variant="outlined"
                label="Qualification"
                value={selectQualification}
                style={{ width: "14vw" }}
                onChange={(e) => setSelectQualification(e.target.value)}
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                size="small"
                variant="outlined"
                label="Reg.No"
                value={selectRegNo}
                style={{ width: "14vw" }}
                onChange={(e) => setSelectRegNo(e.target.value)}
                inputProps={{ maxLength: 50 }}
              />
            </div>
          </div>
          <div style={{ marginTop: "-0.65vw" }}>
            <p className="eMed_Testconfig_RightHead_text">Status</p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingLeft: "0.6vw",
              marginTop: "-0.5vw",
            }}
          >
            <FormControl
              disabled={!selectedEditID}
              className="eMed_room_radio"
            >
              <RadioGroup
                value={selectedStatus}
                sx={{ display: "flex", flexDirection: "row" }}
                onChange={(e) =>
                  setSelectedStatus(e.target.value === "true")
                }
              >
                <FormControlLabel
                  value={true}
                  control={<Radio size="small" />}
                  label="Active"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio size="small" />}
                  label="InActive"
                />
              </RadioGroup>
            </FormControl>
          </div>
          <div style={{ marginTop: "-0.5vw" }}>
            <p className="eMed_Testconfig_RightHead_text">
              Upload Your Signature
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack
              className="eMed_DocRepo_imgDiv"
              justifyContent="center"
              alignItems="center"
              sx={{ backgroundColor: Colors.SecondaryFill }}
            >
              <input
                type="file"
                accept="image/jpg, image/jpeg, image/png"
                onChange={handleFileChange}
                ref={uploadImageRef}
                style={{ display: "none" }}
              />
              <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={1}
              >
                <Stack
                  className="eMed_DocRepo_imgSubDiv"
                  justifyContent="center"
                  alignItems="center"
                  spacing={0.5}
                >
                  <IconButton
                    size="small"
                    disabled={imgUpload.length >= 6}
                    onClick={() => uploadImageRef.current?.click()}
                  >
                    <img
                      id="eMed_upload_img"
                      src={
                        imgUpload.length >= 6
                          ? ImagePaths.LabUploadButton?.default ??
                            ImagePaths.LabUploadButton
                          : ImagePaths.Export?.default ?? ImagePaths.Export
                      }
                      alt="Upload"
                    />
                  </IconButton>
                  <Typography id="eMed_img_lblTxt">
                    {imgUpload.length >= 6 ? "Max upload reached" : "Upload File"}
                  </Typography>
                  {imgUpload.length < 6 && (
                    <Typography
                      id="eMed_img_subTxt"
                      sx={{ marginLeft: "0.5vw" }}
                    >
                      jpg, jpeg, png
                    </Typography>
                  )}
                </Stack>
                {imgUpload.length > 0 && (
                  <Stack
                    className="eMed_Testrep_Img_item"
                    direction="row"
                    justifyContent={
                      imgUpload.length <= 5 ? "center" : "flex-start"
                    }
                    alignItems="center"
                  >
                    {imgUpload.map((image, index) => (
                      <div
                        key={index}
                        className="eMed_Testrep_Img_wrapper"
                      >
                        <Tooltip
                          title={
                            typeof image === "string" ? null : image?.name
                          }
                          arrow
                          placement="top"
                        >
                          <img
                            onClick={() => {
                              if (typeof image === "string") {
                                const ext = (imageURL || "")
                                  .split(".")
                                  .pop()
                                  ?.toLowerCase();
                                if (
                                  ["jpg", "jpeg", "png"].includes(ext)
                                ) {
                                  setPopUpOpen(true);
                                  setSelectedImage(image);
                                } else {
                                  window.open(image);
                                }
                              } else {
                                setPopUpOpen(true);
                                setSelectedImage(URL.createObjectURL(image));
                              }
                            }}
                            src={
                              ImagePaths.ImagePreview?.default ??
                              ImagePaths.ImagePreview
                            }
                            className="eMed_Testrep_Img"
                            alt="Preview"
                          />
                        </Tooltip>
                        <div className="eMed_Testrep_Icon_wrapper">
                          <IconButton
                            id="eMed_Testrep_Icon"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onImageRemove(index);
                            }}
                          >
                            <CancelTwoToneIcon
                              fontSize="small"
                              color="error"
                            />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </div>
          <div className="eMed_TestConfig_RightBtnCon">
            <Stack spacing={2} direction="row">
              <Button
                id="eMed_Btn_Text"
                variant="outlined"
                onClick={clearValue}
              >
                Clear
              </Button>
              <Button
                id="eMed_Btn_Text"
                variant="contained"
                disabled={disableBtn || !editAccess}
                onClick={validation}
              >
                Save
              </Button>
            </Stack>
          </div>
        </div>
      </div>

      {cropPop && src && (
        <Modal open sx={{ outline: "none" }}>
          <Box
            sx={{
              position: "relative",
              width: "100vw",
              height: "100vh",
              outline: "none",
            }}
          >
            <Paper
              elevation={3}
              id="emedhub_vendorDetails_mainBox"
              style={{ width: "60vw", height: "80vh" }}
            >
              <div id="emedhub_vendorDetails_header">
                <Typography>Crop Signature</Typography>
                <IconButton onClick={handleCropClose} size="small">
                  <img
                    className="eMed_DeletePopup_CrossIcon"
                    src={
                      ImagePaths.LabCloseButton?.default ??
                      ImagePaths.LabCloseButton
                    }
                    alt="close"
                  />
                </IconButton>
              </div>
              <Box
                component="div"
                style={{
                  marginTop: "0.75vw",
                  height: "25vw",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "60%",
                      minHeight: "22vw",
                      marginTop: "1.5vw",
                      marginLeft: "1.5vw",
                    }}
                  >
                    <ReactCrop
                      crop={crop}
                      onChange={(c, percentCrop) => setCrop(percentCrop)}
                      onComplete={onCropComplete}
                      aspect={16 / 9}
                      ruleOfThirds
                      style={{ minHeight: "20vw", width: "100%" }}
                    >
                      <img
                        src={src}
                        alt="Crop"
                        style={{ maxHeight: "22vw", width: "auto" }}
                        onLoad={onImageLoad}
                      />
                    </ReactCrop>
                  </div>
                  <div
                    style={{
                      width: "40%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: "-1.5vw",
                      marginRight: "2.5vw",
                    }}
                  >
                    {croppedImageUrl && (
                      <img
                        alt="Crop"
                        style={{ width: "75%", height: "45%" }}
                        src={croppedImageUrl}
                      />
                    )}
                  </div>
                </div>
              </Box>
              <Box
                component="div"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1vw",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  id="eMed_Config_btn"
                  onClick={() => {
                    setCropPop(false);
                    uploadCroppedImg();
                  }}
                >
                  OK
                </Button>
              </Box>
            </Paper>
          </Box>
        </Modal>
      )}

      {popUpOpen && selectedImage && (
        <Modal open sx={{ outline: "none" }}>
          <Box
            sx={{
              position: "relative",
              width: "100vw",
              height: "100vh",
              outline: "none",
            }}
          >
            <Paper
              elevation={3}
              id="emedhub_vendorDetails_mainBox"
              style={{ width: "40vw", height: "70vh" }}
            >
              <div id="emedhub_vendorDetails_header">
                <Typography>Image Preview</Typography>
                <IconButton
                  onClick={() => {
                    setPopUpOpen(false);
                    setSelectedImage(null);
                  }}
                  size="small"
                >
                  <img
                    className="eMed_DeletePopup_CrossIcon"
                    src={
                      ImagePaths.LabCloseButton?.default ??
                      ImagePaths.LabCloseButton
                    }
                    alt="close"
                  />
                </IconButton>
              </div>
              <Box
                component="div"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "90%",
                }}
              >
                <img
                  src={selectedImage}
                  alt="Preview"
                  style={{ width: "45%", height: "45%", objectFit: "contain" }}
                />
              </Box>
            </Paper>
          </Box>
        </Modal>
      )}

      {addnewRoleType && (
        <Dialog
          open
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: "0.5vw" } }}
        >
          <DialogTitle
            className="eMed_CreditPoP_TitleDiv"
            sx={{ borderColor: Colors.themeDark }}
          >
            <Typography marginLeft="1vw" component="span">
              {editRoleName ? "Edit Role Name" : "Add Role Name"}
            </Typography>
            <IconButton
              onClick={() => setAddnewRoleType(false)}
              size="small"
              sx={{ padding: "4px" }}
            >
              <img
                src={
                  ImagePaths.LabCloseButton?.default ??
                  ImagePaths.LabCloseButton
                }
                alt="close"
                className="eMed_action_img_small"
              />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: "2vw" }}>
            <TextField
              inputProps={{ maxLength: 30 }}
              sx={{ width: "100%", marginTop: "2vw", boxSizing: "border-box" }}
              size="small"
              label="Role Name"
              value={addRoleName}
              onChange={(e) => {
                const v = e.target.value;
                if (alphanumValidation(v)) setAddRoleName(v);
              }}
            />
            {editRoleName && (
              <Typography sx={{ fontSize: ".9dvw", color: "#888888", mt: 1 }}>
                Editing this will also affect previous reports.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: "1vw 2vw", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              className="eMed_Filter_Btns"
              onClick={() => setAddRoleName("")}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={isAdd}
              className="eMed_Filter_Btns"
              onClick={sendRoleMaster}
            >
              {renameRole ? "Rename" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isErrorMsg && (
        <ToastMsg
          severity="error"
          msg={isErrorMsgText}
          msgPop={msgClose}
        />
      )}
      {successMsg && (
        <ToastMsg
          severity="success"
          msg={successMsgText}
          msgPop={msgClose}
        />
      )}
      {deletedPopup && (
        <DeletePopup
          DeletTitle="Are You Sure? Do you want to remove this Signature"
          DeleteNotify="This will affect the entire configuration."
          DeletContent="This Signature is linked with various Configuration"
          deleteAlertPopupClose={() => setDeletedPopup(false)}
          removeData={removeDataList}
        />
      )}
      {deleteRole && (
        <ConfirmPopup
          cnfTitle="Confirmation"
          cnfContent="Are you sure, Do you want Delete the Role, It will affect the entire Reports"
          cnfAlertPopupClose={() => setDeleteRole(false)}
          cnfData={deleteRoleApi}
        />
      )}
    </div>
  );
}
